import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SendPushMessageResponse } from "@myapp/contract";
import { FieldWrapper, FormSection } from "../../../shared/ui";
import { FormInput } from "../../../shared/ui/controls/FormInput";
import { Button } from "../../../shared/ui/controls/Button/Button";
import { useSendPushMessage } from "../../../entities/notification";
import {
  sendPushFormDefaults,
  sendPushFormSchema,
  SendPushFormValues,
} from "../model/sendPushFormSchema";
import { buildPushTargetUrl } from "../lib/buildPushTargetUrl";
import { PushTargetField } from "./PushTargetField";

interface Props {
  selectedIds: number[];
}

export const PushComposeForm: React.FC<Props> = ({ selectedIds }) => {
  const sendMutation = useSendPushMessage();
  const [result, setResult] = useState<SendPushMessageResponse | null>(null);

  // `PushTargetField` reads the form through context: the target is four fields that only make
  // sense together, and threading all four plus `setValue` through props buys nothing.
  const form = useForm<SendPushFormValues>({
    resolver: zodResolver(sendPushFormSchema),
    defaultValues: sendPushFormDefaults,
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = form;

  const handleFormSubmit = handleSubmit(async (values) => {
    setResult(null);
    const url = buildPushTargetUrl(values);
    try {
      const response = await sendMutation.mutateAsync({
        userIds: selectedIds,
        title: values.title.trim(),
        body: values.body.trim(),
        ...(url !== undefined && { url }),
      });
      setResult(response);
      reset(sendPushFormDefaults);
    } catch (err: any) {
      const message = err?.response?.data?.message ?? "Nachricht konnte nicht gesendet werden";
      setError("root", { type: "server", message });
    }
  });

  const isPending = isSubmitting || sendMutation.isPending;
  const disabled = selectedIds.length === 0 || isPending;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit}>
        <FormSection title="Nachricht senden">
          {selectedIds.length === 0 && (
            <p className="text-sm font-['PT_Sans'] text-gray-500">
              Keine Empfänger ausgewählt — Nutzer links per Häkchen auswählen.
            </p>
          )}

          <FieldWrapper label="Titel" required error={errors.title?.message}>
            <FormInput name="title" control={control} placeholder="z. B. Werkhof heute früher zu" />
          </FieldWrapper>

          <FieldWrapper label="Text" required error={errors.body?.message}>
            <FormInput
              name="body"
              control={control}
              as="textarea"
              rows={4}
              placeholder="Kurzer Text — auf dem Sperrbildschirm sind 1–2 Zeilen sichtbar."
            />
          </FieldWrapper>

          <PushTargetField />

          {errors.root?.message && (
            <div className="text-[#FB2424] font-['PT_Sans'] text-[14px] italic">
              {errors.root.message}
            </div>
          )}

          {result && (
            <div className="text-sm font-['PT_Sans'] text-primary">
              {`Gesendet an ${result.sentCount} Gerät(e), ${result.failedCount} fehlgeschlagen.`}
              {result.removedCount > 0 && ` ${result.removedCount} tote(s) Gerät(e) entfernt.`}
              {result.skippedUserIds.length > 0 &&
                ` ${result.skippedUserIds.length} Empfänger übersprungen (archiviert oder unbekannt).`}
            </div>
          )}

          {/* The recipient count lives in the label: the button itself says what will happen. */}
          <Button type="submit" variant="primary" disabled={disabled} isLoading={isPending}>
            {selectedIds.length > 0 ? `An ${selectedIds.length} Empfänger senden` : "Senden"}
          </Button>
        </FormSection>
      </form>
    </FormProvider>
  );
};
